import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { BookOpen, Headphones, Mic, PenTool, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_META = {
  listening: { label: "Listening", icon: Headphones },
  speaking:  { label: "Speaking",  icon: Mic },
  reading:   { label: "Reading",   icon: BookOpen },
  writing:   { label: "Writing",   icon: PenTool },
};

const STAGES = [
  { code: "entering",   label: "Entering" },
  { code: "emerging",   label: "Emerging" },
  { code: "developing", label: "Developing" },
  { code: "expanding",  label: "Expanding" },
  { code: "bridging",   label: "Bridging" },
  { code: "reaching",   label: "Reaching" },
];

// Map a MOSAIC domain score (1.0-6.0) to a stage code — this is MOSAIC's own
// internal stage lookup. External sets are rendered as full qualitative ladders;
// no numerical mapping to external systems is produced (UX-1 Option A).
function scoreToStage(score) {
  if (score == null) return "developing";
  const s = parseFloat(score);
  if (isNaN(s)) return "developing";
  if (s < 1.5) return "entering";
  if (s < 2.5) return "emerging";
  if (s < 3.5) return "developing";
  if (s < 4.5) return "expanding";
  if (s < 5.5) return "bridging";
  return "reaching";
}

export default function NPRPanel({ grade, scores }) {
  // scores: { listening, speaking, reading, writing } — MOSAIC per-domain scale
  const [domain, setDomain] = useState("reading");
  const [stage, setStage] = useState(() => scoreToStage(scores?.reading));
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const normalizedGrade = useMemo(() => {
    // Backend accepts K,1..12; the result page may carry 'K','1','2','3-4', etc.
    // Map UI grade-band strings to a representative grade.
    const g = String(grade || "K");
    if (g === "K") return "K";
    if (/^\d+$/.test(g)) return g;
    const m = g.match(/^(\d+)/);
    return m ? m[1] : "K";
  }, [grade]);

  // When domain changes, retrieve the matching MOSAIC stage from the student's scores
  useEffect(() => {
    if (scores && scores[domain] != null) {
      setStage(scoreToStage(scores[domain]));
    }
  }, [domain, scores]);

  useEffect(() => {
    let cancelled = false;
    async function go() {
      try {
        setLoading(true);
        setErr(null);
        const res = await axios.get(`${API}/npr/reference`, {
          params: { grade: normalizedGrade, domain, stage },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message || "Failed to load NPR");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    go();
    return () => { cancelled = true; };
  }, [normalizedGrade, domain, stage]);

  return (
    <div className="border-2 border-gray-200 mb-8" data-testid="npr-panel">
      <div className="p-6 border-b border-gray-200 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
            National Proficiency Reference
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Qualitative reference — see how this student's MOSAIC stage aligns descriptively with NYSESLAT, WIDA ACCESS, ELPA21, Summative ELPAC, and LAS Links. <strong>Not a score conversion.</strong>
          </p>
        </div>
        <div className="flex gap-3 items-center" data-testid="npr-controls">
          <label className="text-xs text-gray-500 flex flex-col">
            Domain
            <select
              className="mt-1 border rounded px-2 py-1 text-sm"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              data-testid="npr-domain-select"
            >
              {Object.entries(DOMAIN_META).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-500 flex flex-col">
            MOSAIC stage
            <select
              className="mt-1 border rounded px-2 py-1 text-sm"
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              data-testid="npr-stage-select"
            >
              {STAGES.map((s) => (
                <option key={s.code} value={s.code}>{s.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {loading && <div className="p-6 text-sm text-gray-500" data-testid="npr-loading">Loading reference…</div>}
      {err && <div className="p-6 text-sm text-red-700" data-testid="npr-error">NPR unavailable: {String(err)}</div>}

      {data && !loading && !err && (
        <div className="p-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* MOSAIC side */}
          <div className="lg:col-span-2" data-testid="npr-mosaic">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-3 h-3 rounded-sm" style={{ background: data.mosaic.color }}></span>
              <h4 className="font-bold text-base">MOSAIC LPA · {data.band_label} · {DOMAIN_META[domain].label} · {data.mosaic.stage_label}</h4>
            </div>
            <div className="grid grid-cols-1 gap-3 text-sm">
              <div data-testid="npr-mosaic-can-do">
                <div className="font-semibold text-xs uppercase tracking-wide text-gray-500 mb-1">Can do</div>
                <ul className="list-disc pl-5 space-y-1">
                  {data.mosaic.can_do.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
              <div data-testid="npr-mosaic-ready-for">
                <div className="font-semibold text-xs uppercase tracking-wide text-gray-500 mb-1">Ready for</div>
                <ul className="list-disc pl-5 space-y-1">
                  {data.mosaic.ready_for.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
              <div data-testid="npr-mosaic-scaffolds">
                <div className="font-semibold text-xs uppercase tracking-wide text-gray-500 mb-1">Scaffolds</div>
                <ul className="list-disc pl-5 space-y-1">
                  {data.mosaic.scaffolds.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
              <div className="text-xs text-gray-500 italic mt-2">
                Stage pattern: <em>{data.mosaic.stage_pattern.support}</em> · {data.mosaic.stage_pattern.complexity}
              </div>
            </div>
          </div>

          {/* External sets */}
          <div className="lg:col-span-3 space-y-5" data-testid="npr-external">
            {Object.entries(data.external_sets).map(([pub, set]) => (
              <div key={pub} data-testid={`npr-ext-${pub}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-sm" style={{ background: set.color }}></span>
                  <strong>{set.publisher}</strong>
                  <span className="text-xs text-gray-400 ml-auto">{set.levels_native.join(" · ")}</span>
                </div>
                <table className="w-full text-xs">
                  <tbody>
                    {set.descriptors.map((cell, i) => (
                      <tr key={i} className="border-b last:border-b-0" data-testid={`npr-ext-${pub}-row-${i}`}>
                        <td className="py-1 pr-3 w-40 align-top font-medium">
                          {cell.native_level_label}
                          {cell.k_reading_amendment_id && (
                            <span className="ml-2 inline-block bg-green-100 text-green-800 font-semibold rounded px-1.5 py-0.5 text-[10px]" data-testid={`kra-tag-${cell.k_reading_amendment_id}`}>
                              {cell.k_reading_amendment_id}
                            </span>
                          )}
                        </td>
                        <td className="py-1 align-top text-gray-700">{cell.descriptor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {set.footnote && (
                  <p className="text-[11px] italic text-gray-500 mt-1" data-testid={`npr-ext-${pub}-footnote`}>{set.footnote}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data && (
        <div className="px-6 pb-5 text-[11px] text-gray-500 border-t pt-3">
          <div className="flex items-start gap-2">
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            <p data-testid="npr-attribution">{data.attribution.approved_attribution}</p>
          </div>
          <p className="mt-1 font-mono opacity-70" data-testid="npr-fingerprint">
            Master {data.master_version} · {data.master_sha256.slice(0, 16)}…
          </p>
        </div>
      )}
    </div>
  );
}
